import { CSSResultGroup, TemplateResult, html } from 'lit';
import { MobileController, OutlineElement } from '@phase2/outline-core';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './outline-search.css.lit';
import outlineButtonBaselineCssLit from '../outline-button-baseline/outline-button-baseline.css.lit';
import '../outline-icon-baseline/outline-icon-baseline';

/**
 * The OutlineSearch component
 * @element outline-search
 */

@customElement('outline-search')
export class OutlineSearch extends OutlineElement {
  static styles: CSSResultGroup = [
    outlineButtonBaselineCssLit,
    componentStyles,
  ];

  private mobileController = new MobileController(this, 'lg');

  @query('.search-wrapper')
  searchWrapper: HTMLElement;

  @query('.search-container')
  searchContainer: HTMLElement;

  @query('.search-button')
  button: HTMLElement;

  /**
   * Indicates whether or not the dropdown is open.
   * You can use this in lieu of the hide method.
   */
  @property({ type: Boolean, reflect: true, attribute: 'expanded' })
  expanded = false;

  /**
   * This is needed to set the initial wrapper width and height
   * based on the contents. If not added the wrapper/container will have incorrect height/width
   */
  initializeWrapperSize() {
    window.requestAnimationFrame(() => {
      const buttonWidth = this.button.offsetWidth;

      this.searchWrapper.style.setProperty('width', buttonWidth + 'px');
      this.searchWrapper.style.setProperty('height', 40 + 'px');
      this.searchContainer.style.setProperty('width', buttonWidth + 'px');
      this.searchContainer.style.setProperty('height', 40 + 'px');
    });
  }

  firstUpdated() {
    window.requestAnimationFrame(() => {
      this.initializeWrapperSize();
    });
  }

  updated() {
    if (!this.searchWrapper.style.width) {
      // re-initializes since width/height
      // attributes are wiped when mobile mode is triggered
      this.initializeWrapperSize();
    }
  }

  hide() {
    this.expanded = false;
  }

  handleClick() {
    this.expanded = !this.expanded;
  }

  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.hide();
    }
  }

  /**
   * Document property here
   */
  @property({ type: String, attribute: 'property-var' })
  propertyVar: string;

  render(): TemplateResult {
    if (this.mobileController.isMobile) {
      return html` <div class="search-wrapper mobile">
        <div class="search-container mobile">
          <outline-button-baseline>
            <button
              class="btn primary small white search-button"
              aria-label="search bar toggle button"
              @keydown="${this.handleKeyDown}"
              @click="${this.handleClick}"
            >
              <outline-icon-baseline
                name="eu-search"
                library="system"
                size="1rem"
              ></outline-icon-baseline>
            </button>
          </outline-button-baseline>
          <slot name="search-form"></slot>
        </div>
      </div>`;
    } else {
      return html` <div class="search-wrapper">
        <div class="search-container ${this.expanded ? 'expanded' : ''}">
          <outline-button-baseline>
            <button
              class="btn link small white search-button"
              aria-label="search bar toggle button"
              @keydown="${this.handleKeyDown}"
              @click="${this.handleClick}"
            >
              ${this.expanded
                ? html`<outline-icon-baseline
                    name="eu-x"
                    library="system"
                    size="1rem"
                  ></outline-icon-baseline>`
                : html`<outline-icon-baseline
                    name="eu-search"
                    library="system"
                    size="1.5rem"
                  ></outline-icon-baseline>`}
            </button>
          </outline-button-baseline>
          <div class="separator"></div>
          <slot name="search-form"></slot>
        </div>
      </div>`;
    }
  }
}
